import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { describeError } from "../utils/error_message";
import { normalizeEmail } from "./auth_validation";
import { errorStatus, login } from "./auth_api";
import { authErrorKey, validateLogin, type FieldErrors } from "./auth_rules";
import { AuthField, FormError, GoogleButton, OrDivider, PRIMARY_BUTTON_CLASS } from "./auth_fields";

const RECOVERY_CLASS = [
	"self-end p-0 border-0 bg-transparent cursor-pointer",
	"text-[13px] text-auth-sub hover:text-primary",
].join(" ");

interface Props {
	onSignedIn: (token: string) => void;
	/** Opens recovery with whatever address is already typed. */
	onForgot: (email: string) => void;
	/** Where Google brings the reader back. */
	returnTo: string;
	onLeaveForGoogle?: () => void;
}

const LoginForm = ({ onSignedIn, onForgot, returnTo, onLeaveForGoogle }: Props) => {
	const { t } = useTranslation();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [errors, setErrors] = useState<FieldErrors>({});
	const [formError, setFormError] = useState("");
	const [loading, setLoading] = useState(false);

	const submit = async (e: FormEvent) => {
		e.preventDefault();
		if (loading) return;
		const found = validateLogin({ email, password });
		setErrors(found);
		setFormError("");
		if (Object.keys(found).length) return;

		setLoading(true);
		try {
			onSignedIn(await login(email, password));
		} catch (error) {
			console.error("Landing login failed", describeError(error));
			setFormError(authErrorKey("login", errorStatus(error)).key);
			setLoading(false);
		}
	};

	return (
		<form className="flex flex-col gap-3" onSubmit={submit} noValidate>
			<AuthField
				type="email"
				name="email"
				required
				autoComplete="username"
				data-testid="auth-email-input"
				placeholder={t("auth-page.inputs.email-placeholder")}
				value={email}
				error={errors.email}
				onChange={e => {
					setEmail(normalizeEmail(e.target.value));
					setErrors(prev => ({ ...prev, email: undefined }));
					setFormError("");
				}}
			/>
			<AuthField
				type="password"
				name="password"
				required
				autoComplete="current-password"
				data-testid="auth-password-input"
				placeholder={t("auth-page.inputs.password-placeholder")}
				value={password}
				error={errors.password}
				onChange={e => {
					setPassword(e.target.value);
					setErrors(prev => ({ ...prev, password: undefined }));
					setFormError("");
				}}
			/>
			<button
				type="button"
				className={RECOVERY_CLASS}
				data-testid="password-recovery-button"
				onClick={() => onForgot(email)}
			>
				{t("auth-page.buttons.password-recovery")}
			</button>
			<FormError error={formError} />
			<button
				type="submit"
				className={PRIMARY_BUTTON_CLASS}
				disabled={loading}
				data-testid="send-form-button"
			>
				{t(loading ? "auth-page.buttons.login-loading" : "auth-page.buttons.login")}
			</button>
			<OrDivider />
			<GoogleButton returnTo={returnTo} onLeave={onLeaveForGoogle}>
				{t("auth-page.buttons.login-with-google")}
			</GoogleButton>
		</form>
	);
};

export default LoginForm;
